import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AlertModal } from "@/components/generic/AlertModal";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import type { Configuration } from "@/types/campaign";
import { store } from "@/utils/jotai";
import {
  createFileRoute,
  Link,
  Outlet,
  useBlocker,
  useChildMatches,
} from "@tanstack/react-router";
import { atom, useAtom } from "jotai";
import { XIcon } from "lucide-react";
import { SetStateAction, useEffect, useMemo } from "react";

const newConfigurationsAtom = atom<Configuration[]>();

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/define/advanced",
)({
  component: RouteComponent,
  staticData: {
    crumb: "Avanceret",
    meta: {
      title: "Avanceret",
    },
  },
  beforeLoad: async ({ context }) => {
    return {
      useAdvancedConfigurations: () => {
        const [_newConfigurations, _setNewConfigurations] = useAtom(
          newConfigurationsAtom,
        );
        const { editCampaign } = context.useEditCampaign();

        const newConfigurations = useMemo(
          () => _newConfigurations || editCampaign.configurations,
          [_newConfigurations, editCampaign.configurations],
        );

        const isDirty = useMemo(
          () => newConfigurations !== editCampaign.configurations,
          [editCampaign.configurations, newConfigurations],
        );

        return useMemo(
          () => ({
            newConfigurations,
            setNewConfigurations: (action: SetStateAction<Configuration[]>) =>
              _setNewConfigurations((prev) =>
                typeof action === "function"
                  ? action(prev || editCampaign.configurations)
                  : action,
              ),
            isDirty,
          }),
          [
            _setNewConfigurations,
            editCampaign.configurations,
            isDirty,
            newConfigurations,
          ],
        );
      },
    };
  },
  loader: () => ({
    showCampaignSetupBottomBar: false,
  }),
  onLeave: () => {
    store.set(newConfigurationsAtom, undefined);
  },
});

function RouteComponent() {
  const routeMatch = Route.useMatch();
  const childMatches = useChildMatches();

  const isContactChildRoute = useMemo(
    () =>
      childMatches.some(
        (match) =>
          match.routeId ===
          "/_dashboard/campaigns/$id/(edit)/define/advanced/$quality/$category/contacts",
      ),
    [childMatches],
  );

  const { useEditCampaign, useAdvancedConfigurations } =
    Route.useRouteContext();
  const { editCampaign } = useEditCampaign();
  const { newConfigurations, setNewConfigurations, isDirty } =
    useAdvancedConfigurations();

  useEffect(() => {
    setNewConfigurations(editCampaign.configurations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCampaign.configurations]);

  const { proceed, reset, status } = useBlocker({
    disabled: !isDirty,
    shouldBlockFn: ({ next }) => {
      if (!isDirty) return false;
      // Allow free navigation within the advanced sub-tree.
      if (
        next.routeId === routeMatch.routeId ||
        next.routeId.startsWith(`${routeMatch.routeId}/`)
      ) {
        return false;
      }
      return true;
    },
    withResolver: true,
  });

  return (
    <>
      <SettingsContainer>
        <div className="flex h-full flex-col gap-y-10">
          <div className="flex items-center justify-between">
            <Breadcrumbs size="sm" from="/campaigns/$id/define" />

            <Link
              to={isContactChildRoute ? "/campaigns/$id/define/advanced" : ".."}
            >
              <XIcon className="size-5" />
            </Link>
          </div>

          <div className="flex h-full flex-col justify-center">
            <Outlet />
          </div>
        </div>
      </SettingsContainer>

      {status === "blocked" && (
        <AlertModal
          title="Du har ikke-gemte ændringer"
          description="Dine ændringer til variationerne vil gå tabt. Er du sikker på at du vil fortsætte?"
          onConfirm={proceed}
          onClose={reset}
        />
      )}
    </>
  );
}
